import React, { memo, useState, useRef } from 'react';
import { string, func, oneOfType, node } from 'prop-types';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Modal from 'components/Modal';
import { ReactComponent as ImageSvg } from 'assets/icons/image.svg';
import { uploadButton as TEXT } from 'locales/en';

const UploadButton = ({
  testId,
  label,
  name,
  placeholder,
  className,
  onSelectFile,
  onDeleteFile,
  error,
  progress,
  labelClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const fileRef = useRef(null);
  const handleUpload = () => {
    fileRef.current?.click();
  };

  const handleSelectFile = (e) => {
    if (e.target.files?.length) {
      onSelectFile(e.target.files[0]);
    }
  };

  const handleDeleteFile = () => {
    setIsOpen(true);
  };

  const _onDeleteFile = () => {
    setIsOpen(false);
    if (onDeleteFile) {
      onDeleteFile();
    }
  };

  return (
    <>
      <label
        htmlFor={name}
        className="mb-4 flex flex-1 flex-col lg:flex-row lg:items-center"
        data-testid={testId}
      >
        <span className={`label ${labelClassName}`}>{label}</span>
        <div className="input-form-wrapper">
          <div className="flex flex-row">
            <div className="relative flex-1 text-javelin-gray-dark">
              {!!placeholder && (
                <span className="absolute top-0 bottom-0 left-0 -mt-0.5 flex h-auto w-11 items-center justify-center">
                  <ImageSvg className="h-4 w-4 fill-current" />
                </span>
              )}
              <input
                type="text"
                className={`input input-full-border truncate rounded pt-2 pl-10 pr-16 text-javelin-gray-dark disabled:bg-white ${
                  !!error ? 'error' : ''
                }`}
                value={placeholder}
                disabled
                name={name}
              />
              {!!placeholder && (progress === 0 || progress === 100) ? (
                <div
                  className="absolute top-0 bottom-0 right-0 -mt-0.5  flex h-11 w-11 cursor-pointer items-center justify-center pr-4"
                  onClick={handleDeleteFile}
                >
                  <span className="mr-2 bg-white text-sm text-javelin-gray-dark text-opacity-40 underline">
                    {TEXT.remove}
                  </span>
                </div>
              ) : (
                <div className="absolute top-0 bottom-0 right-0 flex h-11 w-40  cursor-pointer items-center justify-center rounded-r border border-l-0 border-r-0 border-javelin-inputBorder lg:w-24 xl:w-36">
                  <Button
                    title={TEXT.browse}
                    onClick={handleUpload}
                    className={className}
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    onChange={handleSelectFile}
                    accept=".png, .jpg, .jpeg"
                    data-testid="file-input"
                  />
                </div>
              )}
              <div className="absolute bottom-0 left-0 h-2 w-full">
                <div
                  className={`h-2 rounded-b ${
                    !!progress ? 'transition-width duration-500' : ''
                  } bg-brand-primary`}
                  data-testid="progress-bar"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
          {!!error && <ErrorMessage message={error} />}
        </div>
      </label>
      <Modal
        isOpen={isOpen}
        title={TEXT.modalTitle}
        onCancel={() => setIsOpen(false)}
        onConfirm={_onDeleteFile}
      />
    </>
  );
};

UploadButton.propTypes = {
  className: string,
  testId: string,
  name: string,
  label: oneOfType([string, node]),
  placeholder: string,
  onSelectFile: func,
  error: string,
};
UploadButton.defaultProps = {
  className:
    'bg-javelin-blue box-content border-javelin-blue rounded rounded-l-none h-full px-0 py-px',
  testId: 'upload-button',
  label: '',
  name: 'upload-button',
  placeholder: '',
  onSelectFile: () => null,
  error: null,
  labelClassName: 'pb-1 lg:pb-0 lg:w-4/12',
};

export default memo(UploadButton);
